/**
 * `createRng` — mulberry32 seeded PRNG (W1-01 core-time-math). The seed is shown and editable in the UI so that
 * previews equal results (expression `rand()`, generators, particle jitter). Seeds are taken modulo 2^32
 * (ECMAScript ToUint32), so every number is a valid seed and equal seeds give equal sequences.
 */

import type { CreateRng, Rng } from './types';

/** One mulberry32 step: returns the advanced state and a uniform 32-bit output. */
function step(state: number): readonly [state: number, out: number] {
  const s = (state + 0x6d2b79f5) | 0;
  let t = Math.imul(s ^ (s >>> 15), 1 | s);
  t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
  return [s, (t ^ (t >>> 14)) >>> 0];
}

export const createRng: CreateRng = (seed) => {
  let state = Number.isFinite(seed) ? seed >>> 0 : 0;
  return {
    seed,
    next() {
      const [s, out] = step(state);
      state = s;
      return out / 4294967296;
    },
  };
};

// ---------------------------------------------------------------------------------------------------------------
// Additive helpers
// ---------------------------------------------------------------------------------------------------------------

/** Uniform float in [lo, hi). */
export function rngRange(rng: Rng, lo: number, hi: number): number {
  return lo + (hi - lo) * rng.next();
}

/** Uniform integer in [lo, hi] (inclusive; lo <= hi, both integers). */
export function rngInt(rng: Rng, lo: number, hi: number): number {
  return lo + Math.floor(rng.next() * (hi - lo + 1));
}

/** Fisher-Yates shuffle into a new array. */
export function rngShuffle<T>(rng: Rng, items: readonly T[]): T[] {
  const out = items.slice();
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(rng.next() * (i + 1));
    const tmp = out[i] as T;
    out[i] = out[j] as T;
    out[j] = tmp;
  }
  return out;
}

/** murmur3 finaliser: avalanches a 32-bit integer. */
function fmix32(h0: number): number {
  let h = h0 >>> 0;
  h ^= h >>> 16;
  h = Math.imul(h, 0x85ebca6b);
  h ^= h >>> 13;
  h = Math.imul(h, 0xc2b2ae35);
  h ^= h >>> 16;
  return h >>> 0;
}

/**
 * Deterministic seed from several integers, e.g. `mixSeed(noteId, emissionIndex)` for particles that must be
 * a pure function of their identity (scrubbing reproduces them). Order-sensitive; returns a uint32.
 */
export function mixSeed(...parts: readonly number[]): number {
  let h = 0x9e3779b9;
  for (const p of parts) {
    const v = Number.isFinite(p) ? p >>> 0 : 0;
    // Mix the high bits of large integers too (ids above 2^32 are rare but must not collide trivially).
    const hi = Number.isFinite(p) ? Math.floor(p / 4294967296) >>> 0 : 0;
    h = fmix32(h ^ fmix32(v + 0x632be5ab));
    if (hi !== 0) h = fmix32(h ^ fmix32(hi + 0x5bd1e995));
  }
  return h;
}

/** A fresh random seed (uint32) for "new seed" buttons; uses crypto.getRandomValues. */
export function randomSeed(): number {
  const buf = new Uint32Array(1);
  globalThis.crypto.getRandomValues(buf);
  return buf[0] ?? 0;
}
